import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import Modal from "./Modal";
import supabase from "../../utils/supabase";
import { toast } from "react-toastify";

interface ConfirmDeleteDistrictModalProps {
  isOpen: boolean;
  onClose: () => void;
  district: {
    id: string;
    name: string;
    user_id: string;
    created_at: string;
  } | null;
  onDistrictDeleted: () => void;
}

const ConfirmDeleteDistrictModal: React.FC<ConfirmDeleteDistrictModalProps> = ({
  isOpen,
  onClose,
  district,
  onDistrictDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!district) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("districts")
        .delete()
        .eq("id", district.id);

      if (error) {
        toast.error("Failed to delete district: " + error.message);
        return;
      }

      toast.success("District deleted successfully");
      onDistrictDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting district:", error);
      toast.error("Failed to delete district");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!district) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete District">
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>

        {/* Warning Message */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Are you sure you want to delete this district?
          </h3>
          <p className="text-sm text-gray-600">
            This action cannot be undone. The district and all associated data
            will be permanently removed from the system.
          </p>
        </div>

        {/* District Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-gray-900">{district.name}</div>
            <div className="text-gray-600">
              Created: {new Date(district.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete District
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteDistrictModal;
